// src/components/StudentDetail.tsx
import React, { useContext, useEffect, useState, useRef } from "react";
import { isDevelopmentTimer } from "../utils/timerMode";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { AuthContext } from "../context/AuthContext";
import { getStudent } from "../api/students";
import { getCourses } from "../api/courses";
import { Student, Enrollment, CourseRef } from "../types/student";
import Sidebar from "../components/Sidebar";
import "../styles/StudentDetail.css";
import {
  ArrowLeft,
  Download,
  GraduationCap,
  Mail,
  Phone,
  Star,
  UserRound,
} from "lucide-react";
import { markTopper, unmarkTopper } from "../api/students";
import cktmLogoUrl from "../assets/cktmc.svg";
import stampUrl from "../assets/stampp.png";
import { MobileAccountCard } from "../features/mobileAdmin/accounts/MobileAccountCard";
import { CurrentDeviceCard } from "../features/mobileAdmin/devices/CurrentDeviceCard";
import { EntitlementDiagnosticsPanel } from "../features/mobileAdmin/entitlements/EntitlementDiagnosticsPanel";
import { ContentAccessOperationsPanel } from "../features/mobileAdmin/content/ContentAccessOperationsPanel";
import { SupportNotesPanel } from "../features/mobileAdmin/operations/SupportNotesPanel";

// group definitions for Additional Information
const ADDITIONAL_SECTIONS = [
  {
    title: "Academic Details",
    fields: [
      { key: "schoolName", label: "School Name" },
      { key: "schoolCourse", label: "School Course" },
      { key: "email", label: "Email" },
    ],
  },
  {
    title: "Parent/Guardian",
    fields: [
      { key: "fathersName", label: "Father's Name" },
      { key: "grandfathersName", label: "Grandfather's Name" },
      { key: "guardianNumber", label: "Guardian Number" },
    ],
  },
  {
    title: "Address Details",
    fields: [
      { key: "address", label: "Address" },
      { key: "municipality", label: "Municipality" },
      { key: "wardNo", label: "Ward No." },
      { key: "district", label: "District" },
      { key: "state", label: "State" },
    ],
  },
];

function renderAdditionalValue(val: any) {
  if (val === undefined || val === null || val === "") return "—";
  if (typeof val === "string" || typeof val === "number") return val;
  if (Array.isArray(val)) return val.length === 0 ? "—" : val.join(", ");
  if (typeof val === "object") {
    if ("name" in val) return (val as any).name;
    return JSON.stringify(val, null, 2);
  }
  return String(val);
}

export default function StudentDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [student, setStudent] = useState<Student | null>(null);
  const [courses, setCourses] = useState<CourseRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [isStarred, setIsStarred] = useState(false);
  const idCardRef = useRef<HTMLDivElement>(null);

  // data-URL for org logo (svg) to play nicely with html2canvas
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(cktmLogoUrl);
        const svgText = await res.text();
        const dataUrl =
          "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgText);
        if (!cancelled) setLogoDataUrl(dataUrl);
      } catch (e) {
        console.error("Failed to load org logo SVG", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // starred students from localStorage
  useEffect(() => {
    if (student !== null) {
      setIsStarred(student.isTopper);
    }
  }, [student]);

  // fetch student + courses
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!id) return;
      try {
        const [stu, allCourses] = await Promise.all([
          getStudent(id),
          getCourses(),
        ]);
        if (!mounted) return;
        setStudent(stu);
        setCourses(allCourses);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  if (!user) return <Navigate to="/" replace />;
  if (loading) return <p>Loading…</p>;
  if (!student) return <p>Not found</p>;

  // compute latest course end date
  const validDate = (() => {
    const endDates = student.enrollments
      .map((en) => en.endDate)
      .filter((d): d is string => !!d);
    if (endDates.length === 0) return null;
    return endDates.reduce((a, b) => (new Date(a) > new Date(b) ? a : b));
  })();

  // compute reference number = enrollment id of the latest enrollment
  const referenceNo = (() => {
    if (!validDate) return "—";
    const latest = student.enrollments.find((en) => en.endDate === validDate);
    return latest?.id ?? "—";
  })();

  // PDF download
  const downloadIdCard = async () => {
    if (!idCardRef.current) return;
    try {
      const imgs = idCardRef.current.querySelectorAll("img");
      await Promise.all(
        Array.from(imgs).map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((res) => {
            img.onload = img.onerror = res;
          });
        }),
      );

      const canvas = await html2canvas(idCardRef.current, {
        useCORS: true,
        scale: 2,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(`${student.name.replace(/\s+/g, "_")}_ID_Card.pdf`);
    } catch (err) {
      console.error("Failed to generate PDF", err);
    }
  };

  // photo download
  async function downloadPhoto(stu: Student) {
    if (!stu.image) {
      alert("No photo available to download.");
      return;
    }
    try {
      const resp = await fetch(stu.image, { mode: "cors" });
      if (!resp.ok) throw new Error("Failed to fetch image");
      const blob = await resp.blob();
      const ext = blob.type.split("/")[1] || "png";
      const fname = `${stu.name.replace(/\s+/g, "_")}_photo.${ext}`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fname;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download photo.");
      console.error(err);
    }
  }

  // star toggle
  async function toggleStar() {
    if (!student) return;
    try {
      const updated = isStarred
        ? await unmarkTopper(student.id)
        : await markTopper(student.id);
      setStudent(updated);
      setIsStarred(updated.isTopper);
    } catch (err) {
      console.error("Failed to update topper status", err);
      alert("Could not update topper status.");
    }
  }

  // helper to format dates
  const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString() : "—";

  const actionButton = `
        inline-flex h-10 items-center justify-center gap-2 rounded-[9px] border border-slate-200
        bg-white px-3.5 text-[12px] font-semibold text-slate-700 transition
        hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950
        focus:outline-none focus:ring-2 focus:ring-slate-200
        disabled:cursor-not-allowed disabled:opacity-45
    `;

  const statusClass = (tone: "success" | "warning" | "danger" | "neutral") => {
    if (tone === "success") return "bg-emerald-50 text-emerald-700";
    if (tone === "warning") return "bg-amber-50 text-amber-700";
    if (tone === "danger") return "bg-red-50 text-red-700";
    return "bg-slate-100 text-slate-600";
  };

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <Sidebar />

      <main className="ml-[280px] min-w-0 px-6 py-6 transition-[margin] duration-200 [.sidebar-collapsed_&]:ml-[76px] max-lg:ml-[76px] max-lg:px-4 max-sm:px-3">
        <div className="mx-auto w-full max-w-[1500px] space-y-4">
          {/* Header */}
          <header className="flex items-start justify-between gap-4 max-xl:flex-col max-xl:items-stretch">
            <div className="flex min-w-0 items-start gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-slate-100 text-slate-600">
                <UserRound size={20} strokeWidth={1.8} />
              </div>
              <div className="min-w-0">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">
                  Student management
                </div>
                <h1 className="m-0 truncate text-[28px] font-bold tracking-[-0.03em] text-slate-950 max-sm:text-[24px]">
                  {student.name}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-slate-500">
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    <Mail size={14} className="shrink-0" />
                    <span className="truncate">{student.email}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Phone size={14} />
                    {student.phone || "—"}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={actionButton}
                onClick={() => navigate(-1)}
              >
                <ArrowLeft size={15} /> Back
              </button>
              <button
                type="button"
                className={actionButton}
                onClick={downloadIdCard}
              >
                <Download size={15} /> ID card PDF
              </button>
              <button
                type="button"
                className={actionButton}
                onClick={() => downloadPhoto(student)}
                disabled={!student.image}
                title={
                  !student.image ? "No photo to download" : "Download Photo"
                }
              >
                <Download size={15} /> Photo
              </button>
              <button
                type="button"
                className={`${actionButton} ${isStarred ? "!border-amber-200 !bg-amber-50 !text-amber-700" : ""}`}
                onClick={toggleStar}
                aria-label={isStarred ? "Unset Topper" : "Mark as Topper"}
                title={isStarred ? "Unset Topper" : "Mark as Topper"}
              >
                <Star size={15} fill={isStarred ? "currentColor" : "none"} />
                {isStarred ? "Topper" : "Mark topper"}
              </button>
            </div>
          </header>

          {/* Student overview + ID card */}
          <section className="grid grid-cols-1 items-stretch gap-3 xl:grid-cols-2">
            <article className="flex min-h-[382px] min-w-0 flex-col rounded-[16px] border border-slate-200 bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="m-0 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">
                    Student profile
                  </p>
                  <h2 className="mb-0 mt-1 text-[16px] font-bold text-slate-950">
                    Profile overview
                  </h2>
                </div>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                  {student.enrollments.length} enrollment
                  {student.enrollments.length === 1 ? "" : "s"}
                </span>
              </div>

              <div className="grid flex-1 auto-rows-fr grid-cols-1 gap-2.5 sm:grid-cols-2">
                {[
                  ["Student ID", student.id],
                  [
                    "Date of birth",
                    student.dateOfBirth ? formatDate(student.dateOfBirth) : "—",
                  ],
                  ["Valid until", validDate ? formatDate(validDate) : "—"],
                  ["Address", renderAdditionalValue(student.address)],
                  ["Reference", referenceNo],
                  ["Topper", isStarred ? "Yes" : "No"],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex min-w-0 flex-col justify-center rounded-[11px] border border-slate-100 bg-slate-50/70 px-3 py-2.5"
                  >
                    <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-slate-400">
                      {label}
                    </div>
                    <div
                      className="mt-1 truncate text-[13px] font-semibold text-slate-800"
                      title={String(value)}
                    >
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </article>

            <article className="flex min-h-[382px] min-w-0 flex-col rounded-[16px] border border-slate-200 bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
              <div className="mb-2">
                <p className="m-0 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">
                  Identity
                </p>
                <h2 className="mb-0 mt-1 text-[16px] font-bold text-slate-950">
                  Student ID card
                </h2>
              </div>

              <div className="flex flex-1 items-center justify-center overflow-visible">
                <div className="h-[297px] w-[450px] overflow-visible max-sm:h-auto max-sm:w-full">
                  <div
                    className="w-[500px] max-sm:w-full"
                    style={{
                      transform: "scale(0.9)",
                      transformOrigin: "top left",
                    }}
                  >
                    <section
                      ref={idCardRef}
                      className="id-card"
                      data-ref={`Ref No. : ${referenceNo}`}
                    >
                      <div className="card-header-wrapper">
                        <div className="org-logo">
                          {logoDataUrl ? (
                            <img
                              className="logo-img"
                              src={logoDataUrl}
                              alt="CKTM Logo"
                            />
                          ) : (
                            <div className="logo-img" aria-hidden="true" />
                          )}
                        </div>
                        <div className="card-header-detaiils">
                          <div className="org-info">
                            <h1 className="org-name">
                              {process.env.REACT_APP_COMPANY_NAME ||
                                "Care Kathmandu"}
                            </h1>
                            <p className="org-subtitle">
                              Kathmandu-28, Bagbazar
                            </p>
                            <p className="org-subtitle">Tel: 01-5911560</p>
                          </div>
                        </div>
                      </div>

                      <div className="card-title-section">
                        <div className="card-type">Student Identity Card</div>
                      </div>

                      <div className="card-body flipped">
                        <div className="info-section vertical-list">
                          <div className="info-line">
                            <span className="info-label">Name</span>
                            <span className="info-colon">:</span>
                            <span className="info-value">
                              <strong>{student.name}</strong>
                            </span>
                          </div>
                          <div className="info-line">
                            <span className="info-label">DOB</span>
                            <span className="info-colon">:</span>
                            <span className="info-value">
                              {student.dateOfBirth
                                ? new Date(
                                    student.dateOfBirth,
                                  ).toLocaleDateString()
                                : "—"}
                            </span>
                          </div>
                          <div className="info-line">
                            <span className="info-label">Contact</span>
                            <span className="info-colon">:</span>
                            <span className="info-value">{student.phone}</span>
                          </div>
                          <div className="info-line">
                            <span className="info-label">Address</span>
                            <span className="info-colon">:</span>
                            <span className="info-value">
                              {student.address}
                            </span>
                          </div>
                          <div className="info-line">
                            <span className="info-label">Valid Until</span>
                            <span className="info-colon">:</span>
                            <span className="info-value">
                              {validDate ? formatDate(validDate) : "—"}
                            </span>
                          </div>
                        </div>

                        <div className="photo-section">
                          <div className="photo-wrapper">
                            {student.image ? (
                              <img
                                className="passport-photo"
                                src={student.image}
                                alt={student.name}
                                crossOrigin="anonymous"
                                onError={(e) => {
                                  console.error(
                                    "Failed to load student image with CORS, retrying without:",
                                    student.image,
                                  );
                                  e.currentTarget.removeAttribute(
                                    "crossOrigin",
                                  );
                                  e.currentTarget.onerror = (err) => {
                                    console.error(
                                      "Failed to load student image completely:",
                                      student.image,
                                      err,
                                    );
                                    e.currentTarget.style.display = "none";
                                    const placeholder =
                                      e.currentTarget.parentElement?.querySelector(
                                        ".photo-placeholder.hidden",
                                      ) as HTMLElement;
                                    if (placeholder) {
                                      placeholder.style.display = "flex";
                                      placeholder.classList.remove("hidden");
                                    }
                                  };
                                }}
                              />
                            ) : null}
                            {!student.image && (
                              <div className="photo-placeholder passport-photo">
                                PHOTO
                              </div>
                            )}
                            <div
                              className="photo-placeholder passport-photo hidden"
                              style={{ display: "none" }}
                            >
                              PHOTO
                            </div>
                            <img
                              src={stampUrl}
                              alt="Official Stamp"
                              className="stamp-overlay"
                              onError={(e) => {
                                console.error("Failed to load stamp image");
                                e.currentTarget.style.display = "none";
                              }}
                            />
                            <div className="signature-block">
                              <img
                                src="/sig.png"
                                alt="Principal Signature"
                                className="signature-overlay"
                                onError={(e) => {
                                  console.error(
                                    "Failed to load signature image",
                                  );
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                              <div className="signature-label">Principal</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="card-footer"></div>
                    </section>
                  </div>
                </div>
              </div>
            </article>
          </section>

          {/* Mobile administration */}
          <div className="space-y-4">
            <MobileAccountCard studentId={student.id} />
            <CurrentDeviceCard studentId={student.id} />
            <EntitlementDiagnosticsPanel studentId={student.id} />
            {/* <ContentAccessOperationsPanel studentId={student.id} /> */}
            {/* <SupportNotesPanel studentId={student.id} /> */}
          </div>

          {/* Additional information */}
          <section className="overflow-hidden rounded-[16px] border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
            <div className="border-b border-slate-100 px-5 py-4">
              <p className="m-0 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">
                Student information
              </p>
              <h2 className="mb-0 mt-1 text-[17px] font-bold text-slate-950">
                Additional Information
              </h2>
            </div>
            <div className="space-y-5 p-5">
              {ADDITIONAL_SECTIONS.map((section) => (
                <div key={section.title}>
                  <h3 className="mb-3 mt-0 text-[14px] font-bold text-slate-800">
                    {section.title}
                  </h3>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {section.fields.map((field) => (
                      <div
                        key={field.key}
                        className="min-w-0 rounded-xl border border-slate-100 bg-slate-50/70 px-3.5 py-3"
                      >
                        <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-400">
                          {field.label}
                        </div>
                        <div className="mt-1 break-words text-[14px] font-medium text-slate-700">
                          {renderAdditionalValue(
                            student[field.key as keyof Student],
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Enrollments */}
          <section className="overflow-hidden rounded-[16px] border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
            <div className="border-b border-slate-100 px-5 py-4">
              <p className="m-0 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">
                Academic access
              </p>
              <h2 className="mb-0 mt-1 text-[17px] font-bold text-slate-950">
                Enrollments
              </h2>
            </div>

            <div className="space-y-4 p-5">
              {student.enrollments.map((en: Enrollment) => {
                const courseInfo = courses.find((c) => c.id === en.course.id);
                const now = new Date();
                const accessActive =
                  en.channelAccessStart != null &&
                  en.channelAccessEnd != null &&
                  new Date(en.channelAccessStart) <= now &&
                  now <= new Date(en.channelAccessEnd);

                const channelAccess =
                  en.channelAccessStart && en.channelAccessEnd
                    ? `${formatDate(en.channelAccessStart)} → ${formatDate(en.channelAccessEnd)}`
                    : "—";

                const paymentStatus = en.isFullyPaid
                  ? "Fully paid"
                  : en.paidStatus
                    ? "Partially paid"
                    : "Unpaid";
                const paymentTone: "success" | "warning" | "danger" =
                  en.isFullyPaid
                    ? "success"
                    : en.paidStatus
                      ? "warning"
                      : "danger";
                const accessStatus =
                  en.status === "access_granted"
                    ? "Access granted"
                    : en.status === "grace_used"
                      ? "Grace period"
                      : en.status === "banned"
                        ? "Banned"
                        : "No access";
                const accessTone: "success" | "warning" | "danger" =
                  en.status === "access_granted"
                    ? "success"
                    : en.status === "grace_used"
                      ? "warning"
                      : "danger";
                const accountStatus =
                  en.status === "banned" ? "Banned" : "Not banned";

                return (
                  <article
                    key={en.id}
                    className="overflow-hidden rounded-[14px] border border-slate-200 bg-white"
                  >
                    <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/70 px-4 py-3">
                      <div className="min-w-0">
                        <h3 className="m-0 truncate text-[15px] font-bold text-slate-900">
                          {en.course.name}
                        </h3>
                        <code className="mt-1 block truncate text-[11px] text-slate-400">
                          {en.id}
                        </code>
                      </div>
                      <GraduationCap
                        size={18}
                        className="shrink-0 text-slate-400"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
                      {[
                        ["Base fee", `₹${courseInfo?.baseFee ?? "—"}`],
                        [
                          "Duration",
                          `${courseInfo?.periodMonths ?? "—"} month(s)`,
                        ],
                        ["Discount", `₹${en.discount ?? 0}`],
                        ["Remaining", `₹${en.remaining ?? "—"}`],
                        ["Enrolled date", formatDate(en.enrollDate)],
                        ["Course ends", formatDate(en.endDate)],
                        ["Channel access", channelAccess],
                        [
                          "Grace period",
                          en.graceUsed
                            ? `${en.gracePeriodDays} ${isDevelopmentTimer ? "minute(s)" : "day(s)"}`
                            : "None",
                        ],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          className="min-w-0 rounded-xl border border-slate-100 bg-slate-50/70 px-3.5 py-3"
                        >
                          <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-400">
                            {label}
                          </div>
                          <div className="mt-1 break-words text-[14px] font-semibold text-slate-700">
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap gap-2 border-t border-slate-100 px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${statusClass(paymentTone)}`}
                      >
                        {paymentStatus}
                      </span>
                      <span
                        className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${statusClass(accessTone)}`}
                      >
                        {accessStatus}
                      </span>
                      <span
                        className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${statusClass(accessActive ? "success" : "neutral")}`}
                      >
                        {accessActive ? "Channel active" : "Channel inactive"}
                      </span>
                      <span
                        className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${statusClass(en.status === "banned" ? "danger" : "success")}`}
                      >
                        {accountStatus}
                      </span>
                    </div>
                  </article>
                );
              })}

              {student.enrollments.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-200 px-5 py-10 text-center text-[13px] text-slate-500">
                  No enrollments are available for this student.
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
